import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCurrentUser, type CurrentUser } from '@/api/agent';

interface PortalNavProps {
  searchValue?: string;
  onSearchChange?: (val: string) => void;
  showSearch?: boolean;
}

export default function PortalNav({ searchValue = '', onSearchChange, showSearch = true }: PortalNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [user, setUser] = useState<CurrentUser | null>(null);

  useEffect(() => {
    getCurrentUser().then(setUser).catch(() => {});
  }, []);

  // 从姓名中提取首字母缩写作为头像
  const initials = user?.name
    ? user.name.split(/\s+/).map((s) => s[0]).join('').toUpperCase().slice(0, 2)
    : '';

  // 将时间戳转为日期字符串
  const formatExpiry = (ts: number | undefined): string => {
    if (!ts) return '';
    const ms = ts < 1e12 ? ts * 1000 : ts;
    const d = new Date(ms);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const expiryDate = user?.expirydate ? formatExpiry(user.expirydate) : '';

  const daysUntilExpiry = () => {
    if (!user?.expirydate) return 0;
    const ms = user.expirydate < 1e12 ? user.expirydate * 1000 : user.expirydate;
    const expiry = new Date(ms);
    const today = new Date();
    return Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const days = daysUntilExpiry();
  const isAdmin = user?.accountType?.toLowerCase() === 'admin';

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="sticky top-0 z-40 bg-white border-b border-gray-100" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <div className="flex items-center flex-wrap px-4 md:px-6 gap-2 md:gap-4 py-2 md:py-0 md:h-16">
        {/* Logo */}
        <a
          href="/library"
          className="flex items-center gap-2 shrink-0 cursor-pointer order-1"
          onClick={(e) => { e.preventDefault(); navigate('/library'); }}
        >
          <img
            src="/images/logo-full.png"
            alt="Sinovision Logo"
            className="h-7 md:h-8 w-auto object-contain"
          />
          <span className="text-xs font-semibold text-gray-400 tracking-widest uppercase ml-1 hidden sm:block">
            {t('nav_agent_portal')}
          </span>
        </a>

        {/* 手机端将用户头像推到右侧 */}
        <div className="flex-1 md:hidden order-2" />

        {/* User Avatar Dropdown */}
        <div className="relative order-3 md:order-4" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-1.5 md:gap-2 pl-1 md:pl-2 pr-2 md:pr-3 py-1.5 rounded-full hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ background: '#0069BD' }}>
              {initials}
            </div>
            <div className="hidden sm:block text-left">
              <p className="text-xs font-semibold text-gray-800 leading-tight max-w-[120px] truncate">{user?.name || ''}</p>
              <p className={`text-xs font-medium leading-tight ${days <= 30 ? 'text-amber-500' : 'text-gray-400'}`}>
                Expires {expiryDate}
              </p>
            </div>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              {dropdownOpen ? <i className="ri-arrow-up-s-line text-sm"></i> : <i className="ri-arrow-down-s-line text-sm"></i>}
            </div>
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl border border-gray-100 overflow-hidden z-50" style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
              {/* Header */}
              <div className="px-5 py-4 border-b border-gray-100" style={{ background: '#0069BD0D' }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0" style={{ background: '#0069BD' }}>
                    {initials}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{user?.name || ''}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{user?.email || ''}</p>
                    <p className="text-xs text-gray-500">{user?.company || ''}</p>
                  </div>
                </div>
              </div>

              {/* Expiry Warning */}
              {days <= 30 ? (
                <div className="mx-3 mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center text-amber-500 shrink-0">
                    <i className="ri-error-warning-line text-sm"></i>
                  </div>
                  <p className="text-xs font-semibold text-amber-700">
                    Expires in {days} days ({expiryDate})
                  </p>
                </div>
              ) : null}

              {/* Info */}
              <div className="px-4 py-3 space-y-2">
                <div className="flex items-center justify-between py-1">
                  <span className="text-xs text-gray-500 flex items-center gap-1.5"><i className="ri-shield-check-line"></i>{t('nav_account_type')}</span>
                  <span className="text-xs font-semibold text-gray-800">{user?.accountType || ''}</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <span className="text-xs text-gray-500 flex items-center gap-1.5"><i className="ri-global-line"></i>{t('nav_region')}</span>
                  <span className="text-xs font-semibold text-gray-800">{user?.region || ''}</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <span className="text-xs text-gray-500 flex items-center gap-1.5"><i className="ri-calendar-line"></i>{t('nav_valid_until')}</span>
                  <span className={`text-xs font-semibold ${days <= 30 ? 'text-amber-600' : 'text-gray-800'}`}>
                    {expiryDate}
                    {days > 0 ? ` (${days} days left)` : ' (Expired)'}
                  </span>
                </div>
              </div>

              {/* Admin Panel Entry */}
              {isAdmin && (
                <div className="border-t border-gray-100 px-3 py-2">
                  <button
                    onClick={() => { navigate('/admin'); setDropdownOpen(false); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                    style={{ color: '#0069BD', background: 'rgba(0,105,189,0.04)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,105,189,0.08)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,105,189,0.04)'; }}
                  >
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(0,105,189,0.08)' }}>
                      <i className="ri-dashboard-3-line text-sm" style={{ color: '#0069BD' }}></i>
                    </div>
                    Admin Panel
                  </button>
                </div>
              )}

              {/* Sign Out */}
              <div className="border-t border-gray-100 px-3 py-2">
                <button onClick={() => { localStorage.removeItem('agent_token'); navigate('/login'); }} className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-logout-box-r-line text-sm"></i></div>
                  {t('nav_sign_out')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Search Bar - 手机端独占一行在logo下方 */}
        {showSearch && (
          <div className="w-full md:flex-1 md:max-w-md md:mx-4 order-4 md:order-2 mt-1 md:mt-0">
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-search-line text-sm"></i>
              </div>
              <input
                type="text"
                value={searchValue}
                onChange={(e) => onSearchChange?.(e.target.value)}
                placeholder={t('nav_search_placeholder')}
                className="w-full pl-9 pr-3 md:pr-4 py-2 bg-gray-50 border border-gray-200 rounded-full text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:bg-white transition-all"
                style={{ '--tw-ring-color': '#0069BD' } as React.CSSProperties}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#0069BD'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = ''; }}
              />
            </div>
          </div>
        )}

        {/* 桌面端占位 */}
        <div className="flex-1 hidden md:block order-5 md:order-3" />
      </div>
    </nav>
  );
}
